import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';
import {
  Bell,
  Send,
  Loader2,
  AlertCircle,
  Star,
  Tag,
  Info,
  Zap
} from 'lucide-react';
import { AdminMobileUserAPI, EnhancedMobileUser } from '../../lib/api';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { FormField, FormSection } from '../ui/form-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface SendNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: EnhancedMobileUser;
}

export default function SendNotificationModal({
  isOpen,
  onClose,
  onSuccess,
  user
}: SendNotificationModalProps) {
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    type: 'general',
    category: 'general',
    actionData: {
      action: 'none',
      data: ''
    }
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Notification types and categories
  const notificationTypes = [
    { value: 'general', label: 'General', icon: <Info className="h-4 w-4" /> },
    { value: 'promotion', label: 'Promotion', icon: <Star className="h-4 w-4" /> },
    { value: 'order', label: 'Order Update', icon: <Tag className="h-4 w-4" /> },
    { value: 'alert', label: 'Alert', icon: <AlertCircle className="h-4 w-4" /> },
    { value: 'feature', label: 'New Feature', icon: <Zap className="h-4 w-4" /> }
  ];

  const notificationCategories = [
    'general',
    'promotions',
    'orders',
    'account',
    'app_updates',
    'system'
  ];

  const actionTypes = [
    { value: 'none', label: 'No Action' },
    { value: 'open_app', label: 'Open App' },
    { value: 'open_orders', label: 'Open Orders' },
    { value: 'open_offers', label: 'Open Offers' },
    { value: 'open_profile', label: 'Open Profile' },
    { value: 'open_cart', label: 'Open Cart' },
    { value: 'open_url', label: 'Open URL' }
  ];

  // Predefined notification templates
  const templates = [
    {
      name: 'Welcome Message',
      title: 'Welcome to BuildSupplyHub!',
      body: 'Thank you for joining us. Start exploring our wide range of building materials.',
      type: 'general',
      category: 'general'
    },
    {
      name: 'Special Offer',
      title: 'Special Discount Just for You!',
      body: 'Get 20% off on your next order. Limited time offer!',
      type: 'promotion',
      category: 'promotions'
    },
    {
      name: 'Order Reminder',
      title: 'Complete Your Order',
      body: 'You have items in your cart. Complete your order now!',
      type: 'order',
      category: 'orders'
    },
    {
      name: 'Account Verification',
      title: 'Verify Your Account',
      body: 'Please verify your account to unlock all features.',
      type: 'alert',
      category: 'account'
    }
  ];

  // Update form data
  const updateFormData = (field: string, value: any) => {
    if (field.startsWith('actionData.')) {
      const actionField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        actionData: {
          ...prev.actionData,
          [actionField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Apply template
  const applyTemplate = (template: typeof templates[0]) => {
    setFormData(prev => ({
      ...prev,
      title: template.title,
      body: template.body,
      type: template.type,
      category: template.category
    }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.body.trim()) {
      newErrors.body = 'Message body is required';
    }

    if (formData.actionData.action === 'open_url' && !formData.actionData.data.trim()) {
      newErrors['actionData.data'] = 'URL is required for open URL action';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const notificationData = {
        title: formData.title,
        body: formData.body,
        type: formData.type,
        category: formData.category,
        ...(formData.actionData.action && formData.actionData.action !== 'none' && {
          actionData: {
            action: formData.actionData.action,
            ...(formData.actionData.data && { data: formData.actionData.data })
          }
        })
      };

      const response = await AdminMobileUserAPI.sendNotificationToUser(user._id, notificationData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Notification sent successfully",
          variant: "default"
        });

        handleClose();
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to send notification",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: "Error",
        description: "Failed to send notification. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      title: '',
      body: '',
      type: 'general',
      category: 'general',
      actionData: {
        action: 'none',
        data: ''
      }
    });
    setErrors({});
    onClose();
  };

  return (
    <EnhancedDialog open={isOpen} onOpenChange={handleClose}>
      <EnhancedDialogContent size="lg">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Send Notification
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Send a custom notification to {user.firstName} {user.lastName || ''}
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <EnhancedDialogBody>
            {/* Quick Templates */}
            <FormSection
              title="Quick Templates"
              icon={<Zap className="h-5 w-5" />}
              description="Use predefined templates for common notifications"
            >
              <div className="grid grid-cols-2 gap-2">
                {templates.map((template, index) => (
                  <Button
                    key={index}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyTemplate(template)}
                    className="justify-start"
                  >
                    {template.name}
                  </Button>
                ))}
              </div>
            </FormSection>

            <FormSection
              title="Notification Content"
              icon={<Bell className="h-5 w-5" />}
              description="Customize your notification message"
            >
              <FormField
                label="Title"
                required
                error={errors.title}
                hint="Short, attention-grabbing title"
              >
                <Input
                  value={formData.title}
                  onChange={(e) => updateFormData('title', e.target.value)}
                  placeholder="Enter notification title"
                  maxLength={50}
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {formData.title.length}/50 characters
                </div>
              </FormField>

              <FormField
                label="Message"
                required
                error={errors.body}
                hint="Detailed notification message"
              >
                <Textarea
                  value={formData.body}
                  onChange={(e) => updateFormData('body', e.target.value)}
                  placeholder="Enter notification message"
                  rows={3}
                  maxLength={200}
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {formData.body.length}/200 characters
                </div>
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Type"
                  hint="Notification type for styling"
                >
                  <Select
                    value={formData.type}
                    onValueChange={(value) => updateFormData('type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {notificationTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            {type.icon}
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField
                  label="Category"
                  hint="Notification category for filtering"
                >
                  <Select
                    value={formData.category}
                    onValueChange={(value) => updateFormData('category', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {notificationCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
            </FormSection>

            <FormSection
              title="Action Settings"
              icon={<Tag className="h-5 w-5" />}
              description="Optional action when notification is tapped"
            >
              <FormField
                label="Action Type"
                hint="What happens when user taps the notification"
              >
                <Select
                  value={formData.actionData.action}
                  onValueChange={(value) => updateFormData('actionData.action', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    {actionTypes.map((action) => (
                      <SelectItem key={action.value} value={action.value}>
                        {action.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              {formData.actionData.action === 'open_url' && (
                <FormField
                  label="URL"
                  required
                  error={errors['actionData.data']}
                  hint="URL to open when notification is tapped"
                >
                  <Input
                    value={formData.actionData.data}
                    onChange={(e) => updateFormData('actionData.data', e.target.value)}
                    placeholder="https://example.com"
                    type="url"
                  />
                </FormField>
              )}

              {formData.actionData.action && formData.actionData.action !== 'none' && formData.actionData.action !== 'open_url' && formData.actionData.action !== 'open_app' && (
                <FormField
                  label="Additional Data"
                  hint="Optional data to pass with the action"
                >
                  <Input
                    value={formData.actionData.data}
                    onChange={(e) => updateFormData('actionData.data', e.target.value)}
                    placeholder="Additional data (optional)"
                  />
                </FormField>
              )}
            </FormSection>

            {/* Preview */}
            <FormSection
              title="Preview"
              icon={<Info className="h-5 w-5" />}
              description="How the notification will appear"
            >
              <div className="border rounded-lg p-4 bg-accent/20">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bell className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">
                      {formData.title || 'Notification Title'}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {formData.body || 'Notification message will appear here...'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      BuildSupplyHub • now
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          </EnhancedDialogBody>

          <EnhancedDialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Notification
                </>
              )}
            </Button>
          </EnhancedDialogFooter>
        </form>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
}